import dropdownEnums, { Enums } from "@/services/dropdown-enums";
import { getFieldKey } from "@/services/custom-fields/fieldKeys";
import { getParsedJson } from "@/services/generic.methods";
const titleCaseToCamelCase = (key) => {
  return key?.charAt(0)?.toLowerCase() + key?.slice(1);
};
const mapFieldTypes = (enums, isLowerCase) => {
  const CustomFieldTypes = {};
  enums.forEach((field) => {
    let name = field?.name;
    if (isLowerCase) {
      name = titleCaseToCamelCase(name.toLowerCase());
    }
    CustomFieldTypes[field.value] = name;
  });

  return CustomFieldTypes;
};

const mapField = (field) => {
  const data = getParsedJson(field.data);
  const { enum_Custom_Field_Type, enum_Custom_Field_Sub_Type } = dropdownEnums;
  const fieldTypes = mapFieldTypes(enum_Custom_Field_Type);
  const fieldSubTypes = mapFieldTypes(enum_Custom_Field_Sub_Type, true);
  field.mappedName = field.mappedName;
  if (data?.IsRegex && data?.Regex && typeof data?.Regex === "string") {
    const regexString = data?.Regex;
    // Remove the leading and trailing slashes
    const regexPattern = regexString?.slice(1, -1);

    // Create a RegExp object and assign
    data.Regex = new RegExp(regexPattern);
  }
  const mappedField = {
    ...field,
    title: field.displayName,
    value: "",
    name: getFieldKey(field),
    parentType: fieldTypes?.[field?.fieldType],
    subType: fieldSubTypes?.[field?.fieldSubType],
    isImportant: field.isImportant,
    tooltip: field?.description,
    type: "text",
    className: fieldSubTypes?.[field?.fieldSubType],
    data,
    // rules: { required: field.isImportant },
  };

  mapFieldSubType(mappedField);
  if (field.leadCustomOptions?.length) {
    mappedField.options = mapOptions(field.leadCustomOptions);
  }
  return mappedField;
};

const mapFieldSubType = (mappedField) => {
  let { subType, fieldType } = mappedField;
  subType = subType?.toLowerCase();
  const cFieldsType = Enums.CustomFieldType;
  switch (fieldType) {
    case cFieldsType.SingleValue:
      mapSingleValueSubType(mappedField, subType);
      break;
    case cFieldsType.MultiValue:
      mapMultiValueSubType(mappedField, subType);
      break;
    case cFieldsType.Datetime:
      mapDateTimeSubType(mappedField, subType);
      break;
    case cFieldsType.Text:
      mapTextSubType(mappedField, subType);
      break;
    case Enums.CustomFieldType.Numeric:
      mapNumericSubType(mappedField, subType);
      break;
    default:
      mappedField.type = "text";
  }
};

const mapSingleValueSubType = (mappedField, subType) => {
  if (subType === "dropdown") {
    mappedField.type = "select";
  } else {
    mappedField.type = titleCaseToCamelCase(subType);
  }
};

const mapMultiValueSubType = (mappedField, subType) => {
  if (subType === "multidropdown") {
    mappedField.type = "combo-box";
  } else if (subType === "checkbox") {
    mappedField.type = "checkbox-group";
  }
  mappedField.multiple = true;
  mappedField.value = [];
  return mappedField;
};

const mapDateTimeSubType = (mappedField, subType) => {
  mappedField.type = subType + "-picker";
  if (subType === "dateandtime") {
    mappedField.type = "date-time-picker";
  }
  if (mappedField?.data?.StartFrom) {
    mappedField.min = new Date(mappedField?.data?.StartFrom * 1000);
  }
  if (mappedField?.data?.EndTo) {
    mappedField.max = new Date(mappedField?.data?.EndTo * 1000);
  }
};

const mapTextSubType = (mappedField, subType) => {
  if (subType === "phone") {
    mappedField.type = "phone";
    mappedField.selectName = "countryCode";
    mappedField.rules = {
      ...mappedField.rules,
      validation: "custom",
      pattern: "^(\\+?1[-.\\s]?)?\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}$",
    };
  } else if (subType === "email") {
    mappedField.type = "text";
    mappedField.rules = {
      ...mappedField.rules,
      validation: "email",
    };
    if (mappedField?.data?.IsRegex && mappedField?.data?.Regex) {
      mappedField.rules = {
        ...mappedField.rules,
        validation: "custom",
        pattern: mappedField?.data?.Regex,
      };
    }
  } else if (subType === "ssn") {
    mappedField.type = "ssn";
    mappedField.rules = {
      ...mappedField.rules,
      validation: "custom",
      pattern: "^(?!000|666|9\\d\\d)\\d{3}-(?!00)\\d{2}-(?!0000)\\d{4}$",
      invalidError: "Invalid SSN",
    };
  } else {
    mappedField.type = "text";
  }
};

const getSafeMinMax = (min, max) => {
  // If min or max is undefined, replace with Number.MIN_SAFE_INTEGER or Number.MAX_SAFE_INTEGER respectively
  min = typeof min === "undefined" ? Number.MIN_SAFE_INTEGER : min;
  max = typeof max === "undefined" ? Number.MAX_SAFE_INTEGER : max;

  return { min, max };
};

const mapNumericSubType = (mappedField, subType) => {
  mappedField.type = "number";
  mappedField.rules = {
    ...mappedField.rules,
    validation: "minMaxNumber",
    ...getSafeMinMax(mappedField?.data?.StartFrom, mappedField?.data?.EndTo),
    isBetween: mappedField?.data?.IsBetween,
  };
  if (subType === "currency") {
    mappedField.prefix = "$";
    mappedField.rules = {
      ...mappedField.rules,
      allowDecimal: true,
    };
  }
};

const mapOptions = (options) => {
  return options.map((option) => ({
    ...option,
    value: option.id,
    label: option.value,
  }));
};

export const mappedCustomFields = (fields) => {
  // Separate fields into two arrays based on their type
  const multiValueFields = [];
  const otherFields = [];
  let firstMultipleFound = false;

  // Iterate through each field and classify it
  fields.forEach((field) => {
    const mapped = mapField(field);

    if (field.fieldType === Enums.CustomFieldType.MultiValue) {
      if (!firstMultipleFound) {
        mapped.isFirstClearedField = true; // Add a special property
        firstMultipleFound = true;
      }
      multiValueFields.push(mapped);
    } else {
      otherFields.push(mapped);
    }
  });

  // Combine the other fields with the multi-value fields at the end
  return [...otherFields, ...multiValueFields];
};
